/**
 * TenantBrandingProvider -- React context provider that injects CSS custom
 * properties for per-tenant branding.
 *
 * Fetches branding from the API via tRPC for authenticated users, falls back
 * to platform defaults for unauthenticated users. CSS custom properties are
 * set on document.documentElement so they are available to any component
 * without requiring context consumption.
 *
 * The provider renders children immediately with platform defaults to prevent
 * flash of unstyled content. When API data arrives, CSS properties update
 * live without page reload.
 */
import { createContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { trpc } from "@/core/api";
import { useAuth } from "@/core/auth";
import {
  PLATFORM_DEFAULTS,
  CSS_PROPERTIES,
  type TenantBranding,
  type ResolvedBranding,
} from "./branding.types";

export interface TenantBrandingContextValue {
  /** Resolved primary color (custom or platform default) */
  primaryColor: string;
  /** Resolved accent color (custom or platform default) */
  accentColor: string;
  /** Resolved font family (custom or platform default) */
  fontFamily: string;
  /** Resolved logo URL (custom or empty string) */
  logoUrl: string;
  /** Whether branding data is still loading from the API */
  isLoading: boolean;
}

/**
 * Context is exported so test helpers can construct a compatible provider
 * without requiring tRPC. Tests should import FakeTenantBrandingProvider
 * from tenant-branding.test-helpers.tsx instead of using this directly.
 */
export const TenantBrandingContext = createContext<TenantBrandingContextValue | null>(null);

/**
 * Merge tenant branding with platform defaults.
 * Missing fields fall back to PLATFORM_DEFAULTS.
 */
function resolveBranding(branding?: TenantBranding): ResolvedBranding {
  return {
    primaryColor: branding?.primaryColor ?? PLATFORM_DEFAULTS.primaryColor,
    accentColor: branding?.accentColor ?? PLATFORM_DEFAULTS.accentColor,
    fontFamily: branding?.fontFamily ?? PLATFORM_DEFAULTS.fontFamily,
    logoUrl: branding?.logoUrl ?? PLATFORM_DEFAULTS.logoUrl,
  };
}

/**
 * Apply branding values as CSS custom properties on document.documentElement.
 */
function applyCssProperties(branding: ResolvedBranding): void {
  const root = document.documentElement;
  root.style.setProperty(CSS_PROPERTIES.PRIMARY, branding.primaryColor);
  root.style.setProperty(CSS_PROPERTIES.ACCENT, branding.accentColor);
  root.style.setProperty(CSS_PROPERTIES.FONT, branding.fontFamily);
  // Store the raw URL string -- consumers should wrap with url() in CSS.
  // This avoids issues with special characters in URLs breaking unquoted
  // CSS url() values.
  root.style.setProperty(CSS_PROPERTIES.LOGO_URL, branding.logoUrl);
}

/**
 * Remove branding CSS custom properties from document.documentElement.
 */
function removeCssProperties(): void {
  const root = document.documentElement;
  root.style.removeProperty(CSS_PROPERTIES.PRIMARY);
  root.style.removeProperty(CSS_PROPERTIES.ACCENT);
  root.style.removeProperty(CSS_PROPERTIES.FONT);
  root.style.removeProperty(CSS_PROPERTIES.LOGO_URL);
}

interface TenantBrandingProviderProps {
  children: ReactNode;
}

/**
 * Provider that fetches tenant branding from the API and injects CSS
 * custom properties. Renders children immediately with platform defaults
 * to prevent flash of unstyled content.
 *
 * When the user is not authenticated, the API fetch is skipped and
 * platform defaults are used.
 */
export function TenantBrandingProvider({ children }: TenantBrandingProviderProps) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const prevAuthRef = useRef(isAuthenticated);

  // Only fetch branding when user is authenticated
  const brandingQuery = trpc.tenants.getBranding.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000, // Cache for 1 minute
    refetchOnWindowFocus: false,
  });

  // Use the tRPC-generated query key to avoid coupling to internal key structure
  const brandingQueryKey = getQueryKey(trpc.tenants.getBranding);

  // Invalidate and remove cached branding when auth state changes
  // (e.g. logout or login as different user) to prevent stale/cross-session branding
  useEffect(() => {
    if (prevAuthRef.current !== isAuthenticated) {
      prevAuthRef.current = isAuthenticated;
      if (!isAuthenticated) {
        // User logged out -- remove cached branding data immediately
        queryClient.removeQueries({ queryKey: brandingQueryKey });
      } else {
        // User logged in -- refetch to get correct tenant branding
        queryClient.invalidateQueries({ queryKey: brandingQueryKey });
      }
    }
  }, [isAuthenticated, queryClient, brandingQueryKey]);

  // Gate branding by auth state: only use API data when authenticated,
  // otherwise fall back to platform defaults to prevent stale branding
  // from persisting after logout.
  const apiBranding = isAuthenticated ? brandingQuery.data?.branding : undefined;
  const resolved = useMemo(() => resolveBranding(apiBranding), [apiBranding]);

  // Inject CSS custom properties whenever branding changes.
  // useLayoutEffect runs synchronously before the browser paints, ensuring
  // CSS variables are applied before the first visible frame. This prevents
  // a flash of default/unstyled values that would occur with useEffect.
  useLayoutEffect(() => {
    applyCssProperties(resolved);
    return () => {
      removeCssProperties();
    };
  }, [resolved]);

  const contextValue = useMemo<TenantBrandingContextValue>(
    () => ({
      primaryColor: resolved.primaryColor,
      accentColor: resolved.accentColor,
      fontFamily: resolved.fontFamily,
      logoUrl: resolved.logoUrl,
      isLoading: isAuthenticated && brandingQuery.isLoading,
    }),
    [resolved, isAuthenticated, brandingQuery.isLoading],
  );

  return (
    <TenantBrandingContext.Provider value={contextValue}>{children}</TenantBrandingContext.Provider>
  );
}
